import { useTranslation } from 'react-i18next';

import MenuItem from '@material-ui/core/MenuItem';
import Alert from '@material-ui/lab/Alert';

import CustomInput, { Props as CustomInputProps } from '../../../../components/CustomInput/CustomInput';
import ActionSelect from '../ActionSelect/ActionSelect';
import CategorySelect from '../CategorySelect/CategorySelect';
import MaterialSelect from '../MaterialSelect/MaterialSelect';
import PhaseSelect from '../PhaseSelect/PhaseSelect';
import ProcessSelect from '../ProcessSelect/ProcessSelect';
import UnitOfMeasureSelect from '../UnitOfMeasureSelect/UnitOfMeasureSelect';
import { FlexContainer } from './styled';

interface Props {
    baseInputProps: Omit<CustomInputProps, 'name'>;
    companyId: number;
    processId: number;
    materials: any[];
}

const Form = (props: Props) => {
    const { baseInputProps, companyId, processId } = props;
    const { data } = baseInputProps;

    const { t } = useTranslation();

    const showMaterialSelect = data.__action_type_reductionActionId === 'select_material' && (data?.__allowedMaterialIds ?? []).length !== 1;
    const showAlternativeWeightInput = data.__action_type_reductionActionId === 'select_material' && !data.__weight_unit_of_measure;
    const showAlternativeWeightDisclaimer = showAlternativeWeightInput && data.alternativeWeight && data.alternativeWeight !== '0';

    // Dopo quantityinstock ho tolto <UnitOfMeasureSelect baseInputProps={baseInputProps} />
    /* Eliminato <CustomInput {...baseInputProps} name='replaceableDuration' /> */
    return (
        <>
            <FlexContainer>
                <CustomInput {...baseInputProps} name='description' />

                <CategorySelect baseInputProps={baseInputProps} />

                <MaterialSelect baseInputProps={baseInputProps} onlyPlastic />
            </FlexContainer>

            <CustomInput {...baseInputProps} name='additionalInfo' rows={3} multiline />

            <FlexContainer>
                <CustomInput {...baseInputProps} name='purchaseResponsible' />
                <ProcessSelect baseInputProps={baseInputProps} companyId={companyId} />

                {data.phaseId && (
                    <PhaseSelect baseInputProps={baseInputProps} companyId={companyId} processId={processId} />
                )}

            </FlexContainer>

            <FlexContainer>
                <CustomInput {...baseInputProps} name='quantity' type='number' inputProps={{ min: 0 }} />

                {!!data.id && (
                    <CustomInput {...baseInputProps} name='co2UsageMonthly' inputProps={{ readOnly: true }} />
                )}

                <CustomInput {...baseInputProps} name='quantityInStock' type='number' inputProps={{ min: 0 }} />
                <UnitOfMeasureSelect baseInputProps={baseInputProps} />

                {!data.__weight_unit_of_measure && (
                    <CustomInput {...baseInputProps} name='weight' type='number' inputProps={{ min: 0 }} />
                )}
            </FlexContainer>

            <CustomInput {...baseInputProps} name='acquisition' select >
                <MenuItem value={'direct'} >
                    {t('models.plastic_assessment_item.enum.acquisition.direct')}
                </MenuItem>
                <MenuItem value={'supplier'} >
                    {t('models.plastic_assessment_item.enum.acquisition.supplier')}
                </MenuItem>
            </CustomInput>

            <FlexContainer>
                {(data.alternativeAvailable === '1' || data.alternativeAvailable === true) && (
                    <CustomInput {...baseInputProps} name='alternativeAvailable' select booleanValues>
                        <MenuItem value={'1'} >
                            {t('dialogs.generic.flag_yes')}
                        </MenuItem>
                        <MenuItem value={'0'} >
                            {t('dialogs.generic.flag_no')}
                        </MenuItem>
                    </CustomInput>
                )}

                {/* @ts-ignore */}
                {(data.alternativeAvailable === '1' || data.alternativeAvailable === true) && (
                    <CustomInput {...baseInputProps} name='alternativeCost' />
                )}
            </FlexContainer>

            {(data.alternativeAvailable === '1' || data.alternativeAvailable === true) && (
                <CustomInput {...baseInputProps} name='alternativeDescription' rows={3} multiline />
            )}

            <FlexContainer>
                <CustomInput {...baseInputProps} name='replaceable' select booleanValues>
                    <MenuItem value={'1'} >
                        {t('dialogs.generic.flag_yes')}
                    </MenuItem>
                    <MenuItem value={'0'} >
                        {t('dialogs.generic.flag_no')}
                    </MenuItem>
                </CustomInput>
            </FlexContainer>

            {(data.replaceable === '0' || data.replaceable === false) && (
                <FlexContainer>
                    <CustomInput {...baseInputProps} name='alreadyReplaced' select booleanValues>
                        <MenuItem value={'1'} >
                            {t('dialogs.generic.flag_yes')}
                        </MenuItem>
                        <MenuItem value={'0'} >
                            {t('dialogs.generic.flag_no')}
                        </MenuItem>
                    </CustomInput>
                </FlexContainer>
            )}

            {(data.replaceable === '0' || data.replaceable === false) && (
                <CustomInput {...baseInputProps} name='replaceableReason' rows={3} multiline required />
            )}

            {(!(data.alreadyReplaced === '0' || data.alreadyReplaced === false) && (data.replaceable === '0' || data.replaceable === false)) && (
                <CustomInput {...baseInputProps} name='replacedDetails' rows={3} multiline required />
            )}

            {(data.replaceable === '1' || data.replaceable === true) && (
                <>
                    <FlexContainer>
                        <ActionSelect baseInputProps={baseInputProps} name='reductionActionId' />

                        {showAlternativeWeightInput && !showMaterialSelect && (
                            <CustomInput {...baseInputProps} name='alternativeWeight' type='number' inputProps={{ min: 0 }} />
                        )}
                    </FlexContainer>
                </>
            )}

            {data.__name_materialId?.toUpperCase() === 'HDPE' && data.__name_itemCategoryId?.toLowerCase() === 'sacchi spazzatura' && (
                <Alert style={{ maxWidth: '1200px', margin: '12px 0' }} severity='info'>
                    {t('models.plastic_assessment_item.not_recommended_replacements.warning1')}
                </Alert>
            )}

            {(
                (['PP', 'PS'].includes(data.__name_materialId?.toUpperCase()) && ['forchette', 'coltelli', 'cucchiai'].includes(data.__name_itemCategoryId?.toLowerCase())) ||
                (['PP', 'PET'].includes(data.__name_materialId?.toUpperCase()) && data.__name_itemCategoryId?.toLowerCase() === 'bicchieri')
            ) && (
                    <Alert style={{ maxWidth: '1200px', margin: '12px 0' }} severity='info'>
                        {t('models.plastic_assessment_item.not_recommended_replacements.warning2')}
                    </Alert>
                )}

            {['PP'].includes(data.__name_materialId?.toUpperCase()) && data.__name_itemCategoryId?.toLowerCase() === 'agitatori' && (
                <Alert style={{ maxWidth: '1200px', margin: '12px 0' }} severity='info'>
                    {t('models.plastic_assessment_item.not_recommended_replacements.warning3')}
                </Alert>
            )}

            <FlexContainer>
                {showMaterialSelect && (
                    <MaterialSelect baseInputProps={baseInputProps} name='alternativeMaterial' onlyAllowedMaterisl />
                )}

                {showAlternativeWeightInput && showMaterialSelect && (
                    <CustomInput {...baseInputProps} name='alternativeWeight' type='number' inputProps={{ min: 0 }} />
                )}
            </FlexContainer>

            {showAlternativeWeightDisclaimer && (
                <p className='MuiFormHelperText-root MuiFormHelperText-contained MuiFormHelperText-marginDense' style={{ marginTop: '2px', marginBottom: '6px' }}>
                    {t('models.plastic_assessment_item.attributes.alternative_weight_disclaimer')}
                </p>
            )}

            {(data.replaceable === '1' || data.replaceable === true) && (
                <CustomInput {...baseInputProps} name='actionDetails' rows={3} multiline />
            )}

            {(data.replaceable === '1' || data.replaceable === true) && (
                <>
                    {/*
                    {!isLoading && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={data.createKpi}
                                    onChange={(event) => {
                                        if (handleAttributeChange) {
                                            handleAttributeChange('createKpi', event.target.checked);
                                        }
                                    }}
                                    color='primary'
                                />
                            }
                            label={t('dialogs.plastic_assessment_item_details.create_kpi')}
                        />
                    )}
                    *}

                    {/*
                    {!isLoading && (data.id !== 0 || !data.createKpi) && (
                        <KpiSelect baseInputProps={baseInputProps} companyId={companyId} assessmentId={assessmentId} />
                    )}

                    {!data.createKpi && data.kpiId && (
                        <KpiButton variant='outlined' size='small' onClick={removeKpi}>
                            {t('dialogs.plastic_assessment_item_details.remove_kpi')}
                        </KpiButton>
                    )}
                    */}
                </>
            )}

            {(data.replaceable === '1' || data.replaceable === true) && (
                <>
                    <FlexContainer>
                        <CustomInput {...baseInputProps} name='reductionTarget' type='number' inputProps={{ min: 0, max: 100 }} required />
                        <CustomInput {...baseInputProps} name='dueDate' inputProps={{ maxLength: 10 }} required />
                    </FlexContainer>

                    {(data.createKpi === '1' || data.createKpi === true || true) && (
                        <FlexContainer>
                            <CustomInput {...baseInputProps} name='monitoringFrequency' select >
                                <MenuItem value={'monthly'} >
                                    {t('models.kpi.enum.monitoring_frequency.monthly')}
                                </MenuItem>
                                <MenuItem value={'bimonthly'} >
                                    {t('models.kpi.enum.monitoring_frequency.bimonthly')}
                                </MenuItem>
                                <MenuItem value={'quarterly'} >
                                    {t('models.kpi.enum.monitoring_frequency.quarterly')}
                                </MenuItem>
                            </CustomInput>

                            <CustomInput
                                {...baseInputProps}
                                name='averageConsume'
                                helpText={(data.averageConsume === null || data.averageConsume === undefined || true) ? t('models.plastic_assessment_item.average_consume_not_avilable') : undefined}
                                inputProps={{
                                    readOnly: true,
                                }}
                            />
                        </FlexContainer>
                    )}

                    <FlexContainer>
                        <CustomInput {...baseInputProps} name='actionResponsible' />
                        <CustomInput {...baseInputProps} name='kgCo2EqSavedYearly' inputProps={{
                            readOnly: true,
                        }} />
                    </FlexContainer>
                </>
            )}
        </>
    );
};

export default Form;
